//#region Imports

import {
  AfterViewInit,
  Component,
  ContentChild,
  EventEmitter,
  HostBinding,
  inject,
  Input,
  OnDestroy,
  Output,
  Signal,
  TemplateRef,
} from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { ThemeEnum } from '../../services/theme/models/enums/theme.enum';
import { ThemeService } from '../../services/theme/theme.service';
import { SortIconComponent } from '../sort-icon/sort-icon.component';
import { Sorts } from '../../services/pagination/models/typies/sorts.type';
import { Subscription } from 'rxjs';
import { TablePaginatorComponent } from '../table-paginator/table-paginator.component';

//#endregion

@Component({
    selector: 'l-list-container',
    templateUrl: './list-container.component.html',
    imports: [
        NgTemplateOutlet,
        TablePaginatorComponent,
    ]
})
export class ListContainerComponent<T> implements OnDestroy, AfterViewInit {

  //#region Host Bindings

  @HostBinding('class')
  public classes = 'block border-radius p-5 overflow-hidden';

  //#endregion

  //#region Content Children

  @ContentChild('header', { static: true })
  public header!: TemplateRef<any>;

  @ContentChild('body', { static: true })
  public body!: TemplateRef<any>;

  //#endregion

  //#region Inputs

  @Input({ required: true, alias: 'total' })
  public set sTotal(value: number) {
    this.total = value;
    this.updatePageStatus();
  };

  @Input({ required: true, alias: 'size' })
  public set sSize(value: number) {
    this.size = value;
    this.updatePageStatus();
  }

  @Input({ required: true, alias: 'page' })
  public set sPage(value: number) {
    this.page = value;
    this.updatePageStatus();
  }

  @Input()
  public sort?: Sorts;

  @Input({ required: true })
  public data: T[] = [];

  @Input({ required: true })
  public isLoading: boolean = false;

  //#endregion

  //#region Outputs

  @Output()
  public onPage: EventEmitter<number> = new EventEmitter();

  @Output()
  public onSort: EventEmitter<Sorts> = new EventEmitter();

  //#endregion

  //#region Public Properties

  public total: number = 0;

  public size: number = 0;

  public page: number = 0;

  public isFirstPage: boolean = false;

  public isLastPage: boolean = false;

  public totalPages: number = 1;

  public skeletons = Array.from({ length: 10 });

  private readonly orderSubscriptions: Subscription[] = [];

  private readonly themeService = inject(ThemeService);

  private readonly sortableIcons: SortIconComponent[] = [];

  // eslint-disable-next-line @typescript-eslint/member-ordering
  public theme: Signal<ThemeEnum> = this.themeService.theme;

  //#endregion

  //#region Lifecycle Hooks

  public ngAfterViewInit(): void {
    this.updatePageStatus();
  }

  public ngOnDestroy(): void {
    this.orderSubscriptions.forEach(s => s.unsubscribe());
  }

  //#endregion

  //#region Public Methods

  public firstPage(): void {
    if (this.isFirstPage)
      return;

    this.page = 1;
    this.onPage.emit(this.page);
    this.updatePageStatus();
  }

  public lastPage(): void {
    if (this.isLastPage)
      return;

    this.page = this.totalPages;
    this.onPage.emit(this.page);
    this.updatePageStatus();
  }

  public nextPage(): void {
    this.updatePageStatus();

    if (this.isLastPage)
      return;

    this.page = this.page + 1;
    this.onPage.emit(this.page);
  }

  public prevPage(): void {
    this.updatePageStatus();

    if (this.isFirstPage)
      return;

    this.page = this.page - 1;
    this.onPage.emit(this.page);
  }

  public registerSortableColumn(sortIcon: SortIconComponent): void {
    this.sortableIcons.push(sortIcon);
    this.checkSort();

    const subscription = sortIcon.onChangeOrder.subscribe((value) => {
      this.onOrder(sortIcon.field, value);
    });

    this.orderSubscriptions.push(subscription);
  }

  //#endregion

  //#region Private Methods

  private updatePageStatus(): void {
    const lastPage = Math.max(1, Math.ceil(this.total / this.size));

    this.totalPages = lastPage;
    this.isLastPage = this.page >= lastPage;
    this.isFirstPage = this.page <= 1;
  }

  private checkSort(): void {
    for (const icon of this.sortableIcons) {
      if (icon.field === this.sort?.field)
        icon.simpleOrder = this.sort?.order === 'ASC' ? 1 : -1;
      else
        icon.simpleOrder = 0;
    }
  }

  private onOrder(field: string, order: number): void {
    const sort = this.sortableIcons
      .find(s => s.field !== field && s.simpleOrder !== 0);

    if (sort)
      sort.simpleOrder = 0;

    if (order === 0)
      return void this.onSort.emit(undefined);

    this.onSort.emit({ field, order: order === 1 ? 'ASC' : 'DESC' });
  }

  //#endregion

}
